const ENGLAND_NI = 'england_northern_ireland'
const WALES = 'wales'
const SCOTLAND = 'scotland'

const SCOTTISH_POSTCODE_AREAS = new Set([
  'AB',
  'DD',
  'DG',
  'EH',
  'FK',
  'G',
  'HS',
  'IV',
  'KA',
  'KW',
  'KY',
  'ML',
  'PA',
  'PH',
  'TD',
  'ZE',
])

const WELSH_POSTCODE_AREAS = new Set(['CF', 'LD', 'LL', 'NP', 'SA'])
const WELSH_BORDER_DISTRICTS = new Set([
  'CH5',
  'CH6',
  'CH7',
  'CH8',
  'SY16',
  'SY17',
  'SY18',
  'SY19',
  'SY20',
  'SY21',
  'SY22',
  'SY23',
  'SY24',
  'SY25',
])

export const TAX_JURISDICTIONS = [
  { value: ENGLAND_NI, label: 'England / Northern Ireland', taxName: 'SDLT' },
  { value: WALES, label: 'Wales', taxName: 'LTT' },
  { value: SCOTLAND, label: 'Scotland', taxName: 'LBTT' },
]

export function inferTaxJurisdiction(postcode) {
  const district = postcodeDistrict(postcode)
  const area = district.match(/^[A-Z]+/)?.[0] ?? ''

  if (WELSH_POSTCODE_AREAS.has(area) || WELSH_BORDER_DISTRICTS.has(district)) return WALES
  if (district === 'TD15') return ENGLAND_NI
  if (SCOTTISH_POSTCODE_AREAS.has(area)) return SCOTLAND

  return ENGLAND_NI
}

export function transactionTaxName(jurisdiction) {
  return TAX_JURISDICTIONS.find((entry) => entry.value === jurisdiction)?.taxName ?? 'SDLT'
}

export function calculateTransactionTax(price, {
  jurisdiction = ENGLAND_NI,
  propertyType = 'residential',
  additionalResidentialProperty = false,
} = {}) {
  const amount = Math.max(0, Number(price || 0))

  if (propertyType === 'non_residential') {
    return Math.floor(nonResidentialTax(amount, jurisdiction))
  }

  return Math.floor(residentialTax(amount, jurisdiction, additionalResidentialProperty))
}

function residentialTax(amount, jurisdiction, additionalProperty) {
  if (jurisdiction === WALES) {
    if (additionalProperty && amount >= 40000) {
      return progressiveTax(amount, [
        [180000, 0.05],
        [250000, 0.085],
        [400000, 0.1],
        [750000, 0.125],
        [1500000, 0.15],
        [Infinity, 0.17],
      ])
    }

    return progressiveTax(amount, [
      [225000, 0],
      [400000, 0.06],
      [750000, 0.075],
      [1500000, 0.1],
      [Infinity, 0.12],
    ])
  }

  if (jurisdiction === SCOTLAND) {
    const lbtt = progressiveTax(amount, [
      [145000, 0],
      [250000, 0.02],
      [325000, 0.05],
      [750000, 0.1],
      [Infinity, 0.12],
    ])
    const additionalDwellingSupplement = additionalProperty && amount >= 40000
      ? amount * 0.08
      : 0

    return lbtt + additionalDwellingSupplement
  }

  const surcharge = additionalProperty && amount >= 40000 ? 0.05 : 0

  return progressiveTax(amount, [
    [125000, surcharge],
    [250000, 0.02 + surcharge],
    [925000, 0.05 + surcharge],
    [1500000, 0.1 + surcharge],
    [Infinity, 0.12 + surcharge],
  ])
}

function nonResidentialTax(amount, jurisdiction) {
  if (jurisdiction === WALES) {
    return progressiveTax(amount, [
      [225000, 0],
      [250000, 0.01],
      [1000000, 0.05],
      [Infinity, 0.06],
    ])
  }

  if (jurisdiction === SCOTLAND) {
    return progressiveTax(amount, [
      [150000, 0],
      [250000, 0.01],
      [Infinity, 0.05],
    ])
  }

  return progressiveTax(amount, [
    [150000, 0],
    [250000, 0.02],
    [Infinity, 0.05],
  ])
}

function progressiveTax(amount, bands) {
  let lower = 0

  return bands.reduce((total, [upper, rate]) => {
    const taxable = Math.max(Math.min(amount, upper) - lower, 0)
    lower = upper
    return total + taxable * rate
  }, 0)
}

function postcodeDistrict(value) {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s.+$/, '')
    .match(/^[A-Z]{1,2}\d{1,2}/)?.[0] ?? ''
}
